import React, {
  CSSProperties,
  FocusEvent,
} from 'react';

import { CSSObjectWithLabel } from '@next/react-select';
import AsyncSelect from '@next/react-select/async';
import AsyncCreatable, { AsyncCreatableProps } from '@next/react-select/async-creatable';
import { StylesConfig } from '@next/react-select/dist/declarations/src/styles';

import { theme } from '@waglabs/base-theme';
import {
  Box,
  BoxProps,
} from '@waglabs/box-next';

import {
  FieldHelperText,
  FieldHelperTextProps,
} from '../FieldHelperText';

export type Option = {
  label: string;
  value: unknown;
  extras?: Record<string, string>;
};

export type Styles = StylesConfig<Option, false, {
  label?: string;
  options: Option[];
}>;

export type AutocompleteProps = AsyncCreatableProps<Option, false, {
  label?: string;
  options: Option[];
}> & {
  label?: string;
  error?: boolean;
  helperText?: string;
  helperTextProps?: FieldHelperTextProps;
  rootProps?: BoxProps;
  isDisabled?: boolean;
  isCreatable?: boolean;
};

// This helps overriding certain style function through higher order function
/**
 * @deprecated Use the `styles` prop as it now supports styles composition
 */
export const doOverrideStyles = (
  newStyles: CSSProperties,
) => (
  originalStylesFn: Styles[keyof Styles],
) => (provided: CSSObjectWithLabel, state: any) => {
  const originalStyles = originalStylesFn?.(provided, state) || {};
  return {
    ...originalStyles,
    ...newStyles,
  };
};

// Export the styles to allow consumer to do styles composition
/**
 * @deprecated Use the `styles` prop as it now supports styles composition
 */
export const styles: Styles = {
  control: (provided, state) => ({
    ...provided,
    padding: '16px',
    borderRadius: '8px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: (state.selectProps as unknown as AutocompleteProps).error
      ? theme.colors.red
      : theme.colors.lightGrey,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: theme.colors.darkGray,
    fontFamily: 'muli',
    textAlign: 'start',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: theme.colors.dimGray,
    fontFamily: 'muli',
    textAlign: 'start',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '2px',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
  }),
  input: (provided) => ({
    ...provided,
    padding: 0,
    margin: 0,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '24px',
  }),
  option: (provided: any) => ({
    ...provided,
    textAlign: 'start',
  }),
};

// TODO - Move to wag-react (please!)
// TODO - Support changing of variants
export const Autocomplete = ({
  id,
  name,
  label,
  helperText,
  helperTextProps,
  rootProps, // most of the time we should use `styles`
  onBlur,
  loadOptions,
  options,
  styles: stylesProp,
  isCreatable = false,
  ...rest
}: AutocompleteProps) => {
  const Component = isCreatable ? AsyncCreatable : AsyncSelect;

  return (
    <Box
      {...rootProps}
    >
      <Component
        isClearable
        id={id || name}
        styles={{
          ...stylesProp,
          control: (provided, state) => ({
            ...provided,
            ...styles.control?.(provided, state),
            ...stylesProp?.control,
          }),
          placeholder: (provided, state) => ({
            ...provided,
            ...styles.placeholder?.(provided, state),
            ...stylesProp?.placeholder,
          }),
          singleValue: (provided, state) => ({
            ...provided,
            ...styles.singleValue?.(provided, state),
            ...stylesProp?.singleValue,
          }),
          dropdownIndicator: (provided, state) => ({
            ...provided,
            ...styles.dropdownIndicator?.(provided, state),
            ...stylesProp?.dropdownIndicator,
          }),
          valueContainer: (provided, state) => ({
            ...provided,
            ...styles.valueContainer?.(provided, state),
            ...stylesProp?.valueContainer,
          }),
          input: (provided, state) => ({
            ...provided,
            ...styles.input?.(provided, state),
            ...stylesProp?.input,
          }),
          indicatorsContainer: (provided, state) => ({
            ...provided,
            ...styles.indicatorsContainer?.(provided, state),
            ...stylesProp?.indicatorsContainer,
          }),
        }}
        placeholder={label}
        createOptionPosition="first"
        classNamePrefix="react-select"
        allowCreateWhileLoading={false}
        defaultOptions={options}
        onMenuClose={() => {
          /**
           * Call `onBlur` function on menu close, to potentially trigger
           * any consumer's onBlur logic along with Formik
           */
          const customEvent = {
            target: {
              name,
            },
          };
          /**
           * Wrap into a `setTimeout` to avoid issues on touch devices
           * https://github.com/JedWatson/react-select/issues/3737
           */
          setTimeout(() => {
            onBlur?.(customEvent as unknown as FocusEvent<HTMLInputElement>);
          }, 0);
        }}
        cacheOptions
        loadOptions={loadOptions}
        {...rest}
      />
      {helperText && (
        <FieldHelperText
          text={helperText}
          error={rest.error}
          {...helperTextProps}
        />
      )}
    </Box>
  );
};
